import { useMemo, useState } from 'react';
import { useProducts } from '~/hooks/use-products';

const BADGE_COLORS: Record<string, string> = {
  'Хит': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  'Новинка': 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  'Скидка': 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
};
const DEFAULT_BADGE_COLOR = 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
const ALL_CATEGORY = 'Все';

function formatPrice(price: number) {
  return price.toLocaleString('ru-RU') + ' ₽';
}

export function CatalogPage() {
  const { data: products, isPending, error } = useProducts();
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORY);
  const [added, setAdded] = useState<Record<number, boolean>>({});

  const categories = useMemo(() => {
    const unique = Array.from(new Set((products ?? []).map(p => p.category)));
    return [ALL_CATEGORY, ...unique];
  }, [products]);

  const filtered = (products ?? []).filter(
    p => activeCategory === ALL_CATEGORY || p.category === activeCategory,
  );

  function handleAdd(id: number) {
    setAdded(prev => ({ ...prev, [id]: true }));
    setTimeout(() => setAdded(prev => ({ ...prev, [id]: false })), 1500);
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      {/* Заголовок */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight dark:text-slate-100">
          Каталог товаров
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {products ? `${products.length} товаров в наличии` : ' '}
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400">
          Не удалось загрузить каталог: {error.message}
        </div>
      )}

      {isPending && (
        <div className="py-16 text-center text-sm text-slate-500 dark:text-slate-400">
          Загрузка каталога…
        </div>
      )}

      {products && (
        <>
          {/* Фильтр категорий */}
          <div className="mb-6 flex flex-wrap gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  activeCategory === cat
                    ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                    : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-400 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-300 dark:hover:border-slate-500'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Сетка товаров */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map(product => (
              <div
                key={product.id}
                className="group relative flex flex-col rounded-xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
              >
                {/* Бейдж */}
                {product.badge && (
                  <span className={`absolute right-4 top-4 rounded-full px-2 py-0.5 text-xs font-semibold ${BADGE_COLORS[product.badge] ?? DEFAULT_BADGE_COLOR}`}>
                    {product.badge}
                  </span>
                )}

                {/* Изображение (эмодзи) */}
                <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-xl bg-slate-50 text-4xl dark:bg-slate-800">
                  {product.image}
                </div>

                {/* Контент */}
                <div className="flex flex-1 flex-col">
                  <span className="mb-1 text-xs font-medium text-slate-400 dark:text-slate-500">
                    {product.category}
                  </span>
                  <h2 className="mb-1 font-semibold text-slate-900 dark:text-slate-100">
                    {product.name}
                  </h2>
                  <p className="mb-4 flex-1 text-sm text-slate-500 leading-relaxed dark:text-slate-400">
                    {product.description}
                  </p>

                  {/* Нижняя строка */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      {product.originalPrice && (
                        <span className="text-xs text-slate-400 line-through dark:text-slate-500">
                          {formatPrice(product.originalPrice)}
                        </span>
                      )}
                      <span className={`text-lg font-bold ${product.originalPrice ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-slate-100'}`}>
                        {formatPrice(product.price)}
                      </span>
                    </div>
                    <button
                      onClick={() => handleAdd(product.id)}
                      className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-all text-white ${
                        added[product.id] ? 'bg-green-500' : ''
                      }`}
                      style={!added[product.id] ? { backgroundColor: 'var(--brand)' } : undefined}
                    >
                      {added[product.id] ? '✓ Добавлено' : 'В корзину'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
